export type PolicyRule = {
  id?: string;
  ruleType: string;
  moneyValueMinor?: number | null;
  numericValue?: number | null;
  textValue?: string | null;
  timeFrom?: string | null;
  timeTo?: string | null;
};

export type SimulationTraceInput = {
  outcome?: string | null;
  requiresApproval?: boolean | null;
  violations?: Array<{ ruleType: string; message: string; sourceScope?: string }>;
  effectiveLimits?: Record<string, number | string | boolean | null>;
  message?: string | null;
};

/** The eight platform verticals (03-06). Eligible verticals is one of the seven policy dimensions. */
export const CORPORATE_VERTICALS = [
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'GROCERY', label: 'Grocery' },
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'FLORIST', label: 'Florist' },
  { value: 'CONVENIENCE', label: 'Convenience' },
  { value: 'ELECTRONICS', label: 'Electronics' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'PET', label: 'Pet' },
] as const;

export const WEEKDAYS = [
  { value: 'MON', label: 'Monday', jsDay: 1 },
  { value: 'TUE', label: 'Tuesday', jsDay: 2 },
  { value: 'WED', label: 'Wednesday', jsDay: 3 },
  { value: 'THU', label: 'Thursday', jsDay: 4 },
  { value: 'FRI', label: 'Friday', jsDay: 5 },
  { value: 'SAT', label: 'Saturday', jsDay: 6 },
  { value: 'SUN', label: 'Sunday', jsDay: 0 },
] as const;

export const EXCLUDED_CATEGORY_OPTIONS = [
  { value: 'ALCOHOL', label: 'Alcohol' },
  { value: 'TOBACCO', label: 'Tobacco' },
  { value: 'PHARMACY_MEDICINE', label: 'Pharmacy medicine' },
  { value: 'KNIVES', label: 'Knives' },
  { value: 'SOLVENTS', label: 'Solvents' },
  { value: 'LOTTERY', label: 'Lottery' },
] as const;

/**
 * Rule types the live policy service will persist (MOD-027 POLICY_RULE_TYPES).
 * Weekly is sent only while WEEKLY_CAP is in this list.
 */
export const PERSISTABLE_RULE_TYPES = [
  'PER_ORDER_CAP',
  'DAILY_CAP',
  'WEEKLY_CAP',
  'MONTHLY_CAP',
  'CATEGORY_BLOCK',
  'VERTICAL_BLOCK',
  'TIME_WINDOW',
  'DAY_OF_WEEK',
  'REQUIRES_APPROVAL_ABOVE',
  'MAX_ITEMS',
] as const;

export const WEEKLY_CAP_RULE_TYPE = 'WEEKLY_CAP';

export function isWeeklyCapPersistable(): boolean {
  return (PERSISTABLE_RULE_TYPES as readonly string[]).includes(WEEKLY_CAP_RULE_TYPE);
}

export const SEVEN_POLICY_DIMENSIONS = [
  'limits',
  'eligibleHours',
  'eligibleDays',
  'eligibleVerticals',
  'maxItems',
  'excludedCategories',
  'approvalThreshold',
] as const;

export type PolicyEditorDraft = {
  perOrderCapMinor?: string;
  dailyCapMinor?: string;
  weeklyCapMinor?: string;
  monthlyCapMinor?: string;
  approvalAboveMinor?: string;
  timeFrom?: string;
  timeTo?: string;
  allowedDays?: string[];
  eligibleVerticals?: string[];
  categoryBlock?: string;
  maxItems?: string;
};

export function persistablePolicyRules(rules: PolicyRule[]): PolicyRule[] {
  const allowed = new Set<string>(PERSISTABLE_RULE_TYPES);
  return rules.filter((r) => allowed.has(r.ruleType));
}

export function blockedVerticalsFromEligible(eligible: string[] | undefined): string {
  const selected = new Set((eligible ?? []).map((v) => v.trim().toUpperCase()).filter(Boolean));
  if (selected.size === CORPORATE_VERTICALS.length) return '';
  if (selected.size === 0) {
    return CORPORATE_VERTICALS.map((v) => v.value).join(',');
  }
  return CORPORATE_VERTICALS.map((v) => v.value)
    .filter((v) => !selected.has(v))
    .join(',');
}

export function eligibleVerticalsFromBlocked(blockedCsv: string | undefined): string[] {
  const blocked = new Set(
    (blockedCsv ?? '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  );
  if (blocked.size === 0) return CORPORATE_VERTICALS.map((v) => v.value);
  return CORPORATE_VERTICALS.map((v) => v.value).filter((v) => !blocked.has(v));
}

export function buildPolicyRules(input: PolicyEditorDraft): PolicyRule[] {
  const rules: PolicyRule[] = [];
  const money = (ruleType: string, raw?: string) => {
    const n = raw?.trim() ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n < 0) return;
    rules.push({ ruleType, moneyValueMinor: Math.round(n) });
  };
  money('PER_ORDER_CAP', input.perOrderCapMinor);
  money('DAILY_CAP', input.dailyCapMinor);
  money(WEEKLY_CAP_RULE_TYPE, input.weeklyCapMinor);
  money('MONTHLY_CAP', input.monthlyCapMinor);
  money('REQUIRES_APPROVAL_ABOVE', input.approvalAboveMinor);

  const days = (input.allowedDays ?? []).map((d) => d.trim().toUpperCase()).filter(Boolean);
  const hasHours = Boolean(input.timeFrom?.trim() && input.timeTo?.trim());
  if (days.length > 0 && days.length < WEEKDAYS.length) {
    rules.push({ ruleType: 'DAY_OF_WEEK', textValue: days.join(',') });
  }
  if (hasHours) {
    rules.push({
      ruleType: 'TIME_WINDOW',
      timeFrom: input.timeFrom!.trim(),
      timeTo: input.timeTo!.trim(),
      textValue: null,
    });
  }

  if (input.categoryBlock?.trim()) {
    rules.push({
      ruleType: 'CATEGORY_BLOCK',
      textValue: input.categoryBlock
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .join(','),
    });
  }

  const verticalBlock = blockedVerticalsFromEligible(input.eligibleVerticals);
  if (verticalBlock) {
    rules.push({ ruleType: 'VERTICAL_BLOCK', textValue: verticalBlock });
  }

  const maxItems = input.maxItems?.trim() ? Number(input.maxItems) : NaN;
  if (Number.isFinite(maxItems) && maxItems > 0) {
    rules.push({ ruleType: 'MAX_ITEMS', numericValue: Math.round(maxItems) });
  }
  return rules;
}

export function parseAllowedDays(rules: PolicyRule[] | undefined): string[] {
  const firstClass = rules?.find((r) => r.ruleType === 'DAY_OF_WEEK')?.textValue;
  const legacy = rules?.find((r) => r.ruleType === 'TIME_WINDOW')?.textValue;
  const text = firstClass?.trim() ? firstClass : legacy;
  if (!text?.trim()) return WEEKDAYS.map((d) => d.value);
  return text
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((d) => WEEKDAYS.some((w) => w.value === d));
}

export type EvaluationTraceRow = {
  dimension: string;
  ruleType: string;
  result: 'passed' | 'failed' | 'not-tested';
  detail: string;
};

const TRACE_DIMENSIONS: Array<{
  dimension: string;
  ruleType: string;
  passLabel: string;
  limitKey?: string;
  variant?: 'hours' | 'days';
}> = [
  {
    dimension: 'Per-order limit',
    ruleType: 'PER_ORDER_CAP',
    passLabel: 'Within the per-order cap',
    limitKey: 'perOrderCapMinor',
  },
  {
    dimension: 'Daily limit',
    ruleType: 'DAILY_CAP',
    passLabel: 'Within the daily cap',
    limitKey: 'dailyCapMinor',
  },
  {
    dimension: 'Weekly limit',
    ruleType: 'WEEKLY_CAP',
    passLabel: 'Within the weekly cap',
    limitKey: 'weeklyCapMinor',
  },
  {
    dimension: 'Monthly limit',
    ruleType: 'MONTHLY_CAP',
    passLabel: 'Within the monthly cap',
    limitKey: 'monthlyCapMinor',
  },
  {
    dimension: 'Eligible hours',
    ruleType: 'TIME_WINDOW',
    passLabel: 'Inside the ordering window',
    variant: 'hours',
  },
  {
    dimension: 'Eligible days',
    ruleType: 'DAY_OF_WEEK',
    passLabel: 'On an eligible weekday',
    variant: 'days',
  },
  {
    dimension: 'Eligible verticals',
    ruleType: 'VERTICAL_BLOCK',
    passLabel: 'Vertical is permitted',
  },
  {
    dimension: 'Maximum items',
    ruleType: 'MAX_ITEMS',
    passLabel: 'Item count is within the cap',
    limitKey: 'maxItems',
  },
  {
    dimension: 'Excluded categories',
    ruleType: 'CATEGORY_BLOCK',
    passLabel: 'No excluded categories in the basket',
  },
  {
    dimension: 'Approval threshold',
    ruleType: 'REQUIRES_APPROVAL_ABOVE',
    passLabel: 'Below the approval threshold',
  },
];

function isDayViolation(message: string): boolean {
  return /not allowed on this day|weekday|eligible day/i.test(message);
}

export function buildEvaluationTrace(
  result: SimulationTraceInput | undefined,
  options?: { weeklyCapPresent?: boolean; allowedDaysStored?: boolean; hoursConfigured?: boolean },
): EvaluationTraceRow[] {
  const violations = result?.violations ?? [];
  const limits = result?.effectiveLimits ?? {};
  const dayHit = violations.find(
    (v) =>
      v.ruleType === 'DAY_OF_WEEK' || (v.ruleType === 'TIME_WINDOW' && isDayViolation(v.message)),
  );
  const hourHit = violations.find(
    (v) => v.ruleType === 'TIME_WINDOW' && !isDayViolation(v.message),
  );

  return TRACE_DIMENSIONS.map((dim) => {
    if (dim.variant === 'days') {
      if (dayHit) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'failed',
          detail: dayHit.message,
        };
      }
      if (!result) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'not-tested',
          detail: 'Run the simulation to see this rule.',
        };
      }
      if (!options?.allowedDaysStored) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'not-tested',
          detail: 'No weekday restriction on this policy.',
        };
      }
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'passed',
        detail: dim.passLabel,
      };
    }

    if (dim.variant === 'hours') {
      if (hourHit) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'failed',
          detail: hourHit.message,
        };
      }
      if (!result) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'not-tested',
          detail: 'Run the simulation to see this rule.',
        };
      }
      if (!options?.hoursConfigured) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'not-tested',
          detail: 'No ordering window on this policy.',
        };
      }
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'passed',
        detail: dim.passLabel,
      };
    }

    const hit = violations.find((v) => v.ruleType === dim.ruleType);
    if (hit) {
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'failed',
        detail: hit.message,
      };
    }

    if (dim.ruleType === 'WEEKLY_CAP' && !isWeeklyCapPersistable()) {
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'not-tested',
        detail: options?.weeklyCapPresent
          ? 'Weekly limit is configured but the policy service does not persist or evaluate WEEKLY_CAP yet.'
          : 'No weekly cap on this policy.',
      };
    }

    if (dim.ruleType === 'REQUIRES_APPROVAL_ABOVE') {
      if (result?.requiresApproval) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'failed',
          detail:
            'Basket is above the approval threshold, so the employee would be asked to request approval.',
        };
      }
      if (limits.approvalThresholdMinor != null) {
        return {
          dimension: dim.dimension,
          ruleType: dim.ruleType,
          result: 'passed',
          detail: dim.passLabel,
        };
      }
    }

    if (dim.limitKey && limits[dim.limitKey] != null) {
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'passed',
        detail: dim.passLabel,
      };
    }

    if (!result) {
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'not-tested',
        detail: 'Run the simulation to see this rule.',
      };
    }

    if (dim.ruleType === 'WEEKLY_CAP' && options?.weeklyCapPresent) {
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'passed',
        detail: dim.passLabel,
      };
    }

    if (dim.limitKey) {
      return {
        dimension: dim.dimension,
        ruleType: dim.ruleType,
        result: 'not-tested',
        detail:
          dim.ruleType === 'WEEKLY_CAP'
            ? 'No weekly cap on this policy.'
            : `${dim.dimension} is not on this policy.`,
      };
    }

    return {
      dimension: dim.dimension,
      ruleType: dim.ruleType,
      result: 'passed',
      detail: dim.passLabel,
    };
  });
}

export type DimensionWinner = { dimension: string; note: string };

function moneyMajor(minor: number): string {
  const abs = Math.abs(Math.round(minor));
  return `£${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

function moneyFromRules(rules: PolicyRule[] | undefined, type: string): number | null {
  const v = rules?.find((r) => r.ruleType === type)?.moneyValueMinor;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** BR-APR-003: most-restrictive composition. Cite only — shown so the editor says which level wins. */
export function stricterCompositionNotes(
  draft: PolicyEditorDraft,
  accountRules: PolicyRule[] | undefined,
  scopeType: string,
): DimensionWinner[] {
  if (scopeType === 'ACCOUNT' || !accountRules?.length) return [];
  const notes: DimensionWinner[] = [];
  const compareMoney = (type: string, raw: string | undefined, label: string) => {
    const local = raw?.trim() ? Number(raw) : NaN;
    const account = moneyFromRules(accountRules, type);
    if (!Number.isFinite(local) || account == null) return;
    if (local < account) {
      notes.push({
        dimension: label,
        note: `This policy (${moneyMajor(local)}) is stricter than the account (${moneyMajor(account)}) and will win.`,
      });
    } else if (account < local) {
      notes.push({
        dimension: label,
        note: `The account policy (${moneyMajor(account)}) is stricter and will win over this field (${moneyMajor(local)}).`,
      });
    }
  };
  compareMoney('PER_ORDER_CAP', draft.perOrderCapMinor, 'Per-order limit');
  compareMoney('DAILY_CAP', draft.dailyCapMinor, 'Daily limit');
  compareMoney('WEEKLY_CAP', draft.weeklyCapMinor, 'Weekly limit');
  compareMoney('MONTHLY_CAP', draft.monthlyCapMinor, 'Monthly limit');
  compareMoney('REQUIRES_APPROVAL_ABOVE', draft.approvalAboveMinor, 'Approval threshold');
  const localMax = draft.maxItems?.trim() ? Number(draft.maxItems) : NaN;
  const accountMax = accountRules.find((r) => r.ruleType === 'MAX_ITEMS')?.numericValue;
  if (Number.isFinite(localMax) && typeof accountMax === 'number') {
    if (localMax < accountMax) {
      notes.push({
        dimension: 'Maximum items',
        note: `This policy (${localMax}) is stricter than the account (${accountMax}) and will win.`,
      });
    } else if (accountMax < localMax) {
      notes.push({
        dimension: 'Maximum items',
        note: `The account policy (${accountMax} items) is stricter and will win.`,
      });
    }
  }
  return notes;
}

export function outcomeCopy(result: SimulationTraceInput | undefined): {
  headline: string;
  employeeWouldSee: string;
} {
  const outcome = (result?.outcome ?? '').toUpperCase();
  if (outcome === 'ALLOWED' || outcome === 'ALLOW') {
    return {
      headline: 'Allow',
      employeeWouldSee: 'The corporate payment context stays collapsed. Nothing extra is shown.',
    };
  }
  if (outcome === 'REQUIRES_APPROVAL' || outcome === 'ESCALATE' || result?.requiresApproval) {
    return {
      headline: 'Escalate — approval required',
      employeeWouldSee: 'The breach is explained and requesting approval is one tap.',
    };
  }
  if (outcome === 'BLOCKED' || outcome === 'DENY') {
    return {
      headline: 'Deny — no approval route',
      employeeWouldSee: 'The breach is explained and paying personally is promoted.',
    };
  }
  return {
    headline: result?.outcome ?? 'No result yet',
    employeeWouldSee: result?.message ?? 'Run the simulation to see what the employee would see.',
  };
}
